// 균열(Rift, REBIRTH-MODES-SPEC 3.2): 필드 균열 입구에서 조각 5개로 들어간다.
// 층 = 지역1 스테이지 지도 한 장(a·a2·m·b·b2·f 중 무작위 2~3장, 겹치지 않게). 문은 모두 열려 있고 방 웨이브 대신 몹 무리가 영웅 둘레에 계속 난다(필드 밀도 ×1.5).
// 처치로 진행 게이지가 오르고, 층마다 게이지 몫을 채우면 다음 층 통로가 영웅 앞에 열린다. 마지막 층 100%면 균열 수호자(기존 보스 + 체력 배율),
// 쓰러뜨리면 보상 상자 → 출구(필드 균열 입구 앞으로). 제한 10분, 넘기면 상자 보상 ×0.5. 게이지·시간·층은 층을 넘어도 이어진다.
import { REGIONS } from '../../shared/regions';
import { stageBossHp, type StagePlan } from '../../shared/content/stages';
import { BossState, HeroAct, MobKind, Scene } from '../../shared/types';
import { Cell, type MapDef, type MapGrid } from '../../shared/levels';
import { R1_STAGE_MAPS, R1_STAGE_MAP_KEYS, gridOfMap } from '../../shared/stageMaps';
import type { BossKey, StageMapKey } from '../../shared/levels';
import { RIFT, fieldHpMult } from '../../shared/field/constants';
import { GRIFT_LATE_GOLD, GRIFT_LIMIT_SEC, griftDamageMult, griftFloors, griftGaugeMax, griftGoldMult, griftGuardianHpMult, griftHpMult, griftMilestoneStones, griftNextBest, griftStones } from '../../shared/field/greaterRift';
import { FieldEv, RiftPhase } from '../../shared/field/view';
import type { GameSim } from '../game';
import type { FieldController, RiftState } from './controller';

const TICK_HZ = 60;
const OWNER_RIFT = -3;
const PORTAL_R = 1.8;
const CHEST_R = 1.7;
/** 몹이 날 수 있는 칸 목록(층마다 한 번 만든다 — 장면 전환 때만 할당) */
interface FloorCells { readonly xs: Float32Array; readonly zs: Float32Array; readonly n: number }

export class RiftController {
  private floorKeys: StageMapKey[] = [];
  private cells: FloorCells | null = null;
  private points = 0;
  private spawnClock = 0;
  private late = false;
  private guardianKey: BossKey = 'chieftain';
  private wasInPortal = true;
  private wasInChest = true;
  private alive = 0;
  /** 대균열 판(C12): 0 = 일반 균열. runId = 보상 영수증 */
  private tier = 0;
  private runId = '';
  private gaugeMax: number = RIFT.gaugeMax;
  constructor(private readonly g: GameSim, private readonly field: FieldController) {}

  get state() { return this.field.view.rift; }

  /** 레벨에 맞춘 수호자 체력: 지역1 중간보스(레벨 계단 스테이지) × 배율 */
  guardianHp(level: number): number {
    const idx = Math.max(1, Math.min(10, Math.round(1 + ((level - 1) * 9) / 17)));
    return Math.round((stageBossHp(1, idx, 'mid') * RIFT.guardianHpMult) / 100) * 100;
  }

  /** 대균열 입장(필드 컨트롤러가 열쇠를 쓴 뒤 부른다): 단계 배율·15분 제한·층 3~4 */
  enterGreater(tier: number, runId: string): void {
    this.tier = tier; this.runId = runId;
    this.enterRun(griftFloors(tier), GRIFT_LIMIT_SEC, griftGaugeMax(tier));
    const s = this.state as { tier: number; result: number; milestoneStones: number };
    s.tier = tier; s.result = 0; s.milestoneStones = 0;
    const e = this.g.emit(FieldEv.GriftEnter); e.a = tier; e.b = this.state.floors;
  }

  /** 균열 입장(필드 컨트롤러가 조각을 쓴 뒤 부른다) */
  enter(): void {
    const rng = this.field.rng;
    this.tier = 0; this.runId = '';
    (this.state as { tier: number }).tier = 0;
    this.enterRun(RIFT.floorsMin + Math.floor(rng.next() * (RIFT.floorsMax - RIFT.floorsMin + 1)), RIFT.limitSec, RIFT.gaugeMax);
  }

  private enterRun(floors: number, limitSec: number, gaugeMax: number): void {
    const rng = this.field.rng, s = this.state;
    this.gaugeMax = gaugeMax;
    const pool = [...R1_STAGE_MAP_KEYS];
    this.floorKeys = [];
    for (let i = 0; i < floors; i++) this.floorKeys.push(pool.splice(Math.floor(rng.next() * pool.length), 1)[0]);
    this.guardianKey = rng.next() < 0.5 ? 'chieftain' : 'treant';
    s.phase = RiftPhase.Hunt; s.gauge = 0; s.floor = 0; s.floors = floors; s.elapsedTicks = 0; s.limitTicks = limitSec * TICK_HZ;
    s.portalActive = false; s.chestActive = false; s.rewardGold = 0; s.rewardStones = 0;
    this.points = 0; this.late = false;
    this.loadFloor(0);
  }

  private loadFloor(i: number): void {
    const g = this.g, s = this.state, key = this.floorKeys[i];
    const map: MapDef = R1_STAGE_MAPS[key];
    const grid = gridOfMap(map);
    // 바닥에 남은 드롭은 층을 넘기 전에 거둔다(층 이동이 판 시작이라 바닥 풀이 비워진다)
    if (g.flow.scene === Scene.Rift) g.loot.sweep(true);
    const base = this.field.plan;
    const plan: StagePlan = this.tier > 0 ? { ...base, map, rooms: [], mobHpMult: base.mobHpMult * griftHpMult(this.tier), mobDamageMult: base.mobDamageMult * griftDamageMult(this.tier), envDamageMult: base.envDamageMult * griftDamageMult(this.tier) } : { ...base, map, rooms: [] };
    g.flowController.loadMap('r1', null, {
      scene: Scene.Rift, plan, map, grid,
      label: this.tier > 0 ? `대균열 ${this.tier}단계 · ${i + 1}/${s.floors}층 · ${map.name}` : `균열 ${i + 1}/${s.floors}층 · ${map.name}`,
      objective: this.tier > 0 ? '15분 안에 게이지를 채우고 수호자를 쓰러뜨리세요.' : '균열 몹을 처치해 진행 게이지를 채우세요.',
    });
    if (g.flow.scene !== Scene.Rift) { s.phase = RiftPhase.None; return; }
    this.field.mode = 2;
    // 층은 방 잠금 없이 열린 판이다: 모든 문을 연다
    g.flow.gateOpen.fill(1); g.flowController.gateTarget.fill(1);
    s.floor = i; s.portalActive = false;
    this.cells = floorCells(grid);
    this.spawnClock = 0; this.alive = 0;
    this.wasInPortal = true; this.wasInChest = true;
    this.field.slotOwner.fill(-1);
    const e = g.emit(FieldEv.RiftEnter); e.a = s.floors; e.b = i;
  }

  /** 처치 집계(필드 컨트롤러가 주인 슬롯 처치를 넘긴다) */
  onKill(elite: boolean): void {
    const s = this.state;
    this.alive = Math.max(0, this.alive - 1);
    if (s.phase !== RiftPhase.Hunt) return;
    this.points += elite ? RIFT.gaugePoints.elite : RIFT.gaugePoints.mob;
    s.gauge = Math.min(1, this.points / this.gaugeMax);
  }

  step(): void {
    const g = this.g, s = this.state, h = g.hero;
    if (s.phase === RiftPhase.None) return;
    // 살아 있는 균열 몹 수(주인 표시가 남은 슬롯)
    let alive = 0;
    for (let i = 0; i < this.field.slotOwner.length; i++) if (this.field.slotOwner[i] === OWNER_RIFT) alive++;
    this.alive = alive;
    // 수호자 처치(Chest 전환) 시점에 시계를 멈춘다: 상자를 늦게 열어도 성공 판정·빠른 클리어 보너스가 처치 시각 기준(스펙 3.5)
    // 치명타로 수호자가 Dying에 든 순간에도 멈춘다(사망 연출 90틱 동안 시계가 가서 시간 초과가 되지 않게). 상자 생성 전 연출 지연은 그대로
    const guardianFell = s.phase === RiftPhase.Guardian && (g.boss.state === BossState.Dying || g.boss.state === BossState.Dead);
    if (g.worldTick && s.phase !== RiftPhase.Done && s.phase !== RiftPhase.Chest && !guardianFell) {
      s.elapsedTicks++;
      if (!this.late && s.elapsedTicks > s.limitTicks) { this.late = true; g.flow.objectiveLabel = this.tier > 0 ? '대균열 시간 초과: 이번 판은 기록되지 않는다. 끝까지 가면 위로 상자.' : '제한 시간 초과: 보상이 줄어든다. 수호자를 쓰러뜨리세요.'; }
    }
    if (h.hp <= 0 || h.act === HeroAct.Dead) return;
    if (s.phase === RiftPhase.Hunt) {
      this.spawnPacks();
      const share = (s.floor + 1) / s.floors;
      if (s.floor < s.floors - 1) {
        if (!s.portalActive && s.gauge >= share - 1e-6) this.openPortal();
        if (s.portalActive && this.enterCircle(s.portalX, s.portalZ, PORTAL_R, 'portal')) { this.loadFloor(s.floor + 1); return; }
      } else if (s.gauge >= 1) this.summonGuardian();
      return;
    }
    if (s.phase === RiftPhase.Guardian) {
      const b = g.boss;
      if (b.state === BossState.Dead) {
        s.phase = RiftPhase.Chest; s.chestActive = true;
        const p = this.nearWalkable(b.x, b.z);
        s.chestX = p.x; s.chestZ = p.z;
        g.flow.objectiveLabel = '균열 수호자 처치! 보상 상자를 여세요.';
        this.wasInChest = Math.hypot(h.x - s.chestX, h.z - s.chestZ) <= CHEST_R;
      }
      return;
    }
    if (s.phase === RiftPhase.Chest) {
      if (this.enterCircle(s.chestX, s.chestZ, CHEST_R, 'chest')) this.openChest();
      return;
    }
    if (s.phase === RiftPhase.Done && s.portalActive && this.enterCircle(s.portalX, s.portalZ, PORTAL_R, 'portal')) {
      const rp = this.field.def.riftPortal;
      s.phase = RiftPhase.None; s.portalActive = false; s.chestActive = false;
      // 줍지 않은 상자 드롭(1회성 이정표 강화석 포함)을 맵을 바꾸기 전에 거둔다: 맵 로드가 바닥 풀을 쓸지 않고 지우면 영구 유실된다
      g.loot.sweep(true);
      this.field.enterField({ x: rp.x, z: rp.z + 4 });
    }
  }

  /** 원 안에 새로 들어섰는지(가장자리 판정) */
  private enterCircle(x: number, z: number, r: number, which: 'portal' | 'chest'): boolean {
    const h = this.g.hero, inside = Math.hypot(h.x - x, h.z - z) <= r;
    const was = which === 'portal' ? this.wasInPortal : this.wasInChest;
    if (which === 'portal') this.wasInPortal = inside; else this.wasInChest = inside;
    return inside && !was;
  }

  /** 영웅 둘레에 몹 무리를 채운다(목표 수 = RIFT.population, 영웅에게서 13~30m, 반은 바라보는 쪽 앞) */
  private spawnPacks(): void {
    const g = this.g, h = g.hero, c = this.cells, rng = this.field.rng;
    if (!c || c.n === 0 || !g.worldTick) return;
    if (this.spawnClock > 0) { this.spawnClock--; return; }
    this.spawnClock = 45;
    if (this.alive >= RIFT.population) return;
    const L = this.field.view.mobLevel, hpMult = this.tier > 0 ? griftHpMult(this.tier) : 1;
    for (let t = 0; t < 24; t++) {
      const j = Math.floor(rng.next() * c.n), x = c.xs[j], z = c.zs[j];
      const d = Math.hypot(x - h.x, z - h.z);
      if (d < RIFT.spawnDist[0] || d > RIFT.spawnDist[1]) continue;
      const size = RIFT.packMin + Math.floor(rng.next() * (RIFT.packMax - RIFT.packMin + 1));
      const elite = rng.next() < 0.35;
      let n = 0;
      for (let k = 0; k < size; k++) {
        const a = (k / size) * Math.PI * 2, r = k === 0 ? 0 : 1.8;
        const kind = k === 1 ? MobKind.ShieldGoblin : k === 2 && size >= 6 ? MobKind.Boar : MobKind.Goblin;
        if (this.field.spawnMob(kind, elite && k === 0, x + Math.sin(a) * r, z + Math.cos(a) * r, L, OWNER_RIFT, false, hpMult) >= 0) n++;
      }
      this.alive += n;
      return;
    }
  }

  private openPortal(): void {
    const g = this.g, h = g.hero, s = this.state;
    const p = this.nearWalkable(h.x + Math.sin(h.facing) * 6, h.z + Math.cos(h.facing) * 6);
    s.portalActive = true; s.portalX = p.x; s.portalZ = p.z;
    this.wasInPortal = Math.hypot(h.x - p.x, h.z - p.z) <= PORTAL_R;
    g.flow.objectiveLabel = `다음 층 통로가 열렸다(${s.floor + 2}/${s.floors}층).`;
    const e = g.emit(FieldEv.RiftPortal); e.x = p.x; e.z = p.z; e.a = s.floor + 1;
  }

  private summonGuardian(): void {
    const g = this.g, h = g.hero, s = this.state;
    s.phase = RiftPhase.Guardian;
    // 남은 균열 몹은 거둔다(수호자와 1:1)
    for (let i = 0; i < this.field.slotOwner.length; i++) if (this.field.slotOwner[i] === OWNER_RIFT) this.field.vanish(i);
    let x = h.x, z = h.z - 9;
    for (let t = 0; t < 16; t++) {
      const a = h.facing + (t % 2 ? 1 : -1) * Math.floor((t + 1) / 2) * 0.5;
      const px = h.x + Math.sin(a) * 9, pz = h.z + Math.cos(a) * 9;
      if (g.walkableBoss(px, pz, 2.6)) { x = px; z = pz; break; }
      if (t === 15) { const p = this.nearWalkable(h.x, h.z - 6); x = p.x; z = p.z; }
    }
    const t = this.tier;
    const hp = t > 0 ? Math.round((this.guardianHp(this.field.view.mobLevel) * griftGuardianHpMult(t)) / 100) * 100 : this.guardianHp(this.field.view.mobLevel);
    g.bossController.enter(x, z, this.guardianKey, hp, this.field.plan.mobDamageMult * (t > 0 ? griftDamageMult(t) : 1), false);
    g.boss.name = (t > 0 ? `대균열 ${t}단계 수호자 · ` : '균열 수호자 · ') + g.boss.name;
    g.flow.objectiveLabel = '균열 수호자를 쓰러뜨리세요!';
    const e = g.emit(FieldEv.RiftGuardian); e.x = x; e.z = z; e.a = hp;
  }

  /** 대균열 상자(C12): 시간 안이면 기록(최고 단계·이정표)과 단계 보상, 시간 초과면 위로 상자. 같은 판 id 영수증이 있으면 보상 없음(멱등) */
  private openGreaterChest(): void {
    const g = this.g, s = this.state as RiftState, t = this.tier, gs = this.field.grift;
    const remain = Math.max(0, 1 - s.elapsedTicks / Math.max(1, s.limitTicks));
    const prev = gs.data.best;
    let gold = 0, stones = 0, ms = 0, ok = false;
    if (!this.late) {
      const next = griftNextBest(prev, t, Math.max(s.cap, t), remain, false);
      const r = gs.recordClear(this.runId, next);
      ok = r.ok;
      if (ok) for (const m of r.milestones) ms += griftMilestoneStones(m);
    } else ok = gs.recordLate(this.runId);
    if (ok) {
      const base = REGIONS.r1.reward.base * RIFT.chestGoldMult * fieldHpMult(this.field.view.mobLevel) * g.stats.goldMult;
      gold = Math.round(base * griftGoldMult(t) / RIFT.chestGoldMult * (this.late ? GRIFT_LATE_GOLD : 1));
      stones = this.late ? 2 : griftStones(t) + ms;
      if (gold > 0) g.save.addGold(gold);
      g.loot.dropStones(s.chestX, s.chestZ, stones);
      // 상자: 엘리트 드롭 굴림 — 시간 안이면 단계 10마다 한 번 더(최대 4)
      const rolls = this.late ? 1 : 1 + Math.min(3, Math.floor(t / 10));
      for (let i = 0; i < rolls; i++) g.loot.onMobKilled(s.chestX, s.chestZ, true);
    }
    s.rewardGold = gold; s.rewardStones = stones;
    s.result = this.late ? 2 : 1; s.prevBest = prev; s.newBest = gs.data.best; s.best = gs.data.best; s.milestoneStones = ms;
    s.selectable = Math.min(s.cap, s.best + 1);
    s.chestActive = false; s.phase = RiftPhase.Done;
    const p = this.nearWalkable(s.chestX, s.chestZ + 3.5);
    s.portalActive = true; s.portalX = p.x; s.portalZ = p.z;
    this.wasInPortal = Math.hypot(g.hero.x - p.x, g.hero.z - p.z) <= PORTAL_R;
    g.flow.objectiveLabel = this.late
      ? `대균열 ${t}단계: 시간 초과 — 위로 상자 골드 ${gold}. 출구로 필드에 돌아가세요.`
      : `대균열 ${t}단계 돌파! 최고 기록 ${gs.data.best}단계${ms ? ` · 이정표 강화석 +${ms}` : ''}. 출구로 필드에 돌아가세요.`;
    const e = g.emit(FieldEv.GriftResult); e.a = t; e.b = gs.data.best; e.c = this.late ? 0 : 1; e.d = ms;
    const r2 = g.emit(FieldEv.RiftReward); r2.a = gold; r2.b = stones; r2.c = this.late ? 0 : 1;
  }

  private openChest(): void {
    if (this.tier > 0) { this.openGreaterChest(); return; }
    const g = this.g, s = this.state;
    const mult = this.late ? RIFT.lateRewardMult : 1;
    const gold = Math.round(REGIONS.r1.reward.base * RIFT.chestGoldMult * fieldHpMult(this.field.view.mobLevel) * mult * g.stats.goldMult);
    const stones = Math.max(1, Math.round(RIFT.chestStones * mult));
    if (gold > 0) g.save.addGold(gold);
    g.loot.dropStones(s.chestX, s.chestZ, stones);
    // 상자: 엘리트 처치 드롭 한 번 더(장비 확률 굴림, 기존 드롭 표)
    g.loot.onMobKilled(s.chestX, s.chestZ, true);
    this.field.save.addRiftClear();
    s.rewardGold = gold; s.rewardStones = stones; s.clears = this.field.save.data.riftClears;
    s.chestActive = false; s.phase = RiftPhase.Done;
    const p = this.nearWalkable(s.chestX, s.chestZ + 3.5);
    s.portalActive = true; s.portalX = p.x; s.portalZ = p.z;
    this.wasInPortal = Math.hypot(g.hero.x - p.x, g.hero.z - p.z) <= PORTAL_R;
    g.flow.objectiveLabel = `보상: 골드 ${gold} · 강화석 ${stones}${this.late ? '(시간 초과 ×0.5)' : ''}. 출구로 필드에 돌아가세요.`;
    const e = g.emit(FieldEv.RiftReward); e.a = gold; e.b = stones; e.c = this.late ? 0 : 1;
  }

  /** (x, z)에서 가장 가까운 걸을 수 있는 칸 중심(둘레 8m까지) */
  private nearWalkable(x: number, z: number): { x: number; z: number } {
    const g = this.g;
    if (g.walkable(x, z, 0.6, -1, true)) return { x, z };
    for (let r = 1; r <= 8; r++) for (let i = 0; i < 16; i++) {
      const a = (i * Math.PI) / 8, px = x + Math.sin(a) * r, pz = z + Math.cos(a) * r;
      if (g.walkable(px, pz, 0.6, -1, true)) return { x: px, z: pz };
    }
    return { x: g.hero.x, z: g.hero.z };
  }
}

/** 몹이 날 수 있는 칸(바닥이고 둘레 1칸도 바닥) 중심 목록 */
function floorCells(grid: MapGrid): FloorCells {
  const xs: number[] = [], zs: number[] = [];
  const W = grid.width, H = grid.height, c = grid.cells;
  for (let z = 1; z < H - 1; z++) for (let x = 1; x < W - 1; x++) {
    let ok = true;
    for (let dz = -1; dz <= 1 && ok; dz++) for (let dx = -1; dx <= 1; dx++) if (c[(z + dz) * W + x + dx] !== Cell.Floor) { ok = false; break; }
    if (ok && grid.lavaCells[z * W + x] === 0) { xs.push(grid.originX + x + 0.5); zs.push(grid.originZ + z + 0.5); }
  }
  return { xs: Float32Array.from(xs), zs: Float32Array.from(zs), n: xs.length };
}
